import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertTriangle, Info, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSuperAdminAccess } from '@/hooks/useSuperAdminAccess';
import { FLW_PUBLIC_KEY } from '@/utils/constants';
import { subscriptionService } from '@/utils/subscriptionService';

interface UpgradeErrorHandlerProps {
  onRetry?: () => void;
}

export const UpgradeErrorHandler: React.FC<UpgradeErrorHandlerProps> = ({ onRetry }) => {
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const { user } = useSuperAdminAccess();

  useEffect(() => {
    checkAccessAndRunDiagnostics();
  }, []);

  const checkAccessAndRunDiagnostics = async () => {
    try {
      // First check if user is SuperAdmin
      const superAdminStatus = await subscriptionService.isUserSuperAdmin();
      setHasAccess(superAdminStatus);
      
      if (!superAdminStatus) {
        setLoading(false);
        return;
      }
      
      // Only run diagnostics if user has access
      await runDiagnostics();
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
      setLoading(false);
    }
  };

  const runDiagnostics = async () => {
    setLoading(true);
    try {
      const results = await performUpgradeDiagnostics();
      setDiagnostics(results);
    } catch (error) {
      console.error('Diagnostics failed:', error);
      setDiagnostics({ error: 'Diagnostics failed' });
    } finally {
      setLoading(false);
    }
  };

  const performUpgradeDiagnostics = async () => {
    const results: any = {
      timestamp: new Date().toISOString(),
      user: null,
      subscription: null,
      plans: null,
      environment: null,
      errors: []
    };

    try {
      // Check user authentication
      if (!user) {
        results.errors.push('User not authenticated');
      } else {
        results.user = {
          id: user.id,
          email: user.email,
          hasMetadata: !!user.user_metadata
        };
      }

      // Check subscription data
      try {
        const subscription = await subscriptionService.getCurrentPlan();
        results.subscription = subscription ? {
          name: subscription.name,
          price: subscription.price,
          currency: subscription.currency,
          billing_interval: subscription.billing_interval,
          hasFeatures: !!subscription.features,
          features: subscription.features
        } : null;
      } catch (error) {
        results.errors.push(`Subscription check failed: ${error}`);
      }

      // Check available plans
      try {
        const plans = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('is_active', true);
        
        results.plans = {
          count: plans.data?.length || 0,
          plans: plans.data?.map(p => ({
            id: p.id,
            name: p.name,
            price: p.price,
            currency: p.currency,
            billing_interval: p.billing_interval
          }))
        };
      } catch (error) {
        results.errors.push(`Plans check failed: ${error}`);
      }

      // Check environment variables
      // Vite syntax (commented out for webpack compatibility)
      // results.environment = {
      //   hasFlutterwaveKey: !!(import.meta.env.VITE_FLW_PUBLIC_KEY || 'FLWPUBK_TEST-1234567890abcdef1234567890abcdef-X'),
      //   flutterwaveKeyLength: (import.meta.env.VITE_FLW_PUBLIC_KEY || 'FLWPUBK_TEST-1234567890abcdef1234567890abcdef-X').length,
      //   isTestKey: (import.meta.env.VITE_FLW_PUBLIC_KEY || 'FLWPUBK_TEST-1234567890abcdef1234567890abcdef-X').includes('TEST')
      // };
      
      // Using centralized constants
      results.environment = {
        hasFlutterwaveKey: !!FLW_PUBLIC_KEY,
        flutterwaveKeyLength: FLW_PUBLIC_KEY.length,
        isTestKey: FLW_PUBLIC_KEY.includes('TEST')
      };

      // Check database connection
      try {
        const { data, error } = await supabase
          .from('user_subscriptions')
          .select('count')
          .limit(1);
        
        results.database = {
          connected: !error,
          error: error?.message
        };
      } catch (error) {
        results.errors.push(`Database check failed: ${error}`);
      }

    } catch (error) {
      results.errors.push(`General diagnostics failed: ${error}`);
    }

    return results;
  };

  const getStatusIcon = (status: boolean) => {
    return status ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <AlertTriangle className="h-4 w-4 text-red-500" />
    );
  };

  const getStatusBadge = (status: boolean) => {
    return (
      <Badge variant={status ? "default" : "destructive"}>
        {status ? "OK" : "ERROR"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Running Diagnostics...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't render anything for non-SuperAdmin users
  if (!hasAccess) {
    return null;
  }

  if (!diagnostics) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Diagnostics Failed</AlertTitle>
        <AlertDescription>
          Unable to run upgrade diagnostics. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5" />
          Upgrade Diagnostics
        </CardTitle>
        <CardDescription>
          Detailed analysis of potential upgrade issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* User Authentication */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">User Authentication</h3>
            {getStatusBadge(!!diagnostics.user)}
          </div>
          {diagnostics.user ? (
            <div className="text-sm text-gray-600">
              <div>User ID: {diagnostics.user.id}</div>
              <div>Email: {diagnostics.user.email}</div>
              <div>Has Metadata: {diagnostics.user.hasMetadata ? 'Yes' : 'No'}</div>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>User not authenticated</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Current Subscription */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Current Subscription</h3>
            {getStatusBadge(!!diagnostics.subscription)}
          </div>
          {diagnostics.subscription ? (
            <div className="text-sm text-gray-600">
              <div>Plan: {diagnostics.subscription.name}</div>
              <div>Price: {diagnostics.subscription.price} {diagnostics.subscription.currency}</div>
              <div>Billing: {diagnostics.subscription.billing_interval}</div>
              <div>Has Features: {diagnostics.subscription.hasFeatures ? 'Yes' : 'No'}</div>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>No active subscription found</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Available Plans */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Available Plans</h3>
            {getStatusBadge(diagnostics.plans?.count > 0)}
          </div>
          {diagnostics.plans?.count > 0 ? (
            <div className="text-sm text-gray-600">
              <div>Total Plans: {diagnostics.plans.count}</div>
              <div className="mt-2">
                {diagnostics.plans.plans?.map((plan: any, index: number) => (
                  <div key={index} className="flex justify-between">
                    <span>{plan.name} ({plan.billing_interval})</span>
                    <span>{plan.price} {plan.currency}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>No subscription plans found</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Environment Configuration */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Environment Configuration</h3>
            {getStatusBadge(diagnostics.environment?.hasFlutterwaveKey)}
          </div>
          <div className="text-sm text-gray-600">
            <div>Flutterwave Key: {diagnostics.environment?.hasFlutterwaveKey ? 'Configured' : 'Missing'}</div>
            <div>Key Length: {diagnostics.environment?.flutterwaveKeyLength} characters</div>
            <div>Test Mode: {diagnostics.environment?.isTestKey ? 'Yes' : 'No'}</div>
          </div>
        </div>

        {/* Database Connection */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Database Connection</h3>
            {getStatusBadge(diagnostics.database?.connected)}
          </div>
          {diagnostics.database?.connected ? (
            <div className="text-sm text-gray-600">Connected successfully</div>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{diagnostics.database?.error || 'Database connection failed'}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Errors */}
        {diagnostics.errors.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold text-red-600">Errors Found</h3>
            {diagnostics.errors.map((error: string, index: number) => (
              <Alert key={index} variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={runDiagnostics} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Re-run Diagnostics
          </Button>
          {onRetry && (
            <Button onClick={onRetry}>
              Try Upgrade Again
            </Button>
          )}
        </div>


      </CardContent>
    </Card>
  );
}; 